/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ALL_PRODUCT_TYPES,
  MPC_PRODUCT_LEGEND_SWATCH_COLOR
} from "constants/chart.constants";
import { MultiProductFeature } from "types/charts/feature";
import { isBoeFamilyProduct } from "utils/chart/productNameHelpers";
import {
  getSeriesLabelAliases,
  seriesMatchesProduct
} from "utils/chart/seriesMatchesProduct";

import { AxisProductSegment } from "models/chart";

import { getProductOverrideColor } from "./getEffectiveSeriesColor";
import {
  SWATCH_REFERENCE_WIDTH,
  SWATCH_WIDTH,
  buildProductStyleMap,
  resolveProductLabel,
  resolveProductLegendLabel
} from "./mpcTranslatorHelpers";
import { formatChartTitleWithSubscript } from "./utils";

const SWATCH_HEIGHT = 10;
// Literal spaces before an inline swatch — ECharts ignores image-segment padding
// and empty rich-segment width on axis names, so plain-text spacing is reliable.
const NAME_TO_SWATCH_GAP = "  ";

/** SVG dashed-line swatch as a data URI, dash array scaled to the inline swatch width. */
export function buildSwatchDataUri(
  lineStyleType: string | number[],
  color: string
): string {
  const y = SWATCH_HEIGHT / 2;
  let dashAttr = "";
  if (lineStyleType !== "solid" && Array.isArray(lineStyleType)) {
    const scaled = lineStyleType.map((seg) =>
      Math.max(1, Math.round(seg * (SWATCH_WIDTH / SWATCH_REFERENCE_WIDTH)))
    );
    dashAttr = `stroke-dasharray='${scaled.join(" ")}'`;
  }
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${SWATCH_WIDTH}' height='${SWATCH_HEIGHT}'>` +
    `<line x1='0' y1='${y}' x2='${SWATCH_WIDTH}' y2='${y}' stroke='${color}' stroke-width='2' ${dashAttr}/>` +
    `</svg>`;
  return "data:image/svg+xml," + encodeURIComponent(svg);
}

/** Neutral swatch colour, or the product's override colour when bin-colours are on. */
function swatchColor(product: string, override: boolean): string {
  return (
    (override ? getProductOverrideColor(product) : undefined) ??
    MPC_PRODUCT_LEGEND_SWATCH_COLOR
  );
}

/** ECharts rich-text image segment rendering one line-style swatch. */
function swatchRich(
  lineStyleType: string | number[],
  color: string
): Record<string, any> {
  return {
    height: SWATCH_HEIGHT,
    width: SWATCH_WIDTH,
    backgroundColor: { image: buildSwatchDataUri(lineStyleType, color) }
  };
}

const ALL_PRODUCT_ENTRIES = Object.values(ALL_PRODUCT_TYPES) as Array<{
  key: string;
  label: string;
}>;

/** Map a product label OR key to its canonical ALL_PRODUCT_TYPES key (e.g. the
 * label "Oil + Cond" and the key "Oil" both → "Oil"). Returns the input unchanged
 * when it isn't a known product. */
function toCanonicalProductKey(product: string): string {
  const entry =
    ALL_PRODUCT_ENTRIES.find((p) => p.label === product) ??
    ALL_PRODUCT_ENTRIES.find((p) => p.key === product);
  return entry?.key ?? product;
}

/**
 * Resolve a backend axis segment to the internal style-map key of the CUM PRODUCT
 * the axis plots against, or undefined when that product isn't selected.
 *
 * Resolves from `seg.label` (the cleaned cum-product name, e.g. "Oil + Cond",
 * "Total (O+C+W)") — deliberately NOT `seg.productKey`. The backend sets productKey
 * to the first series matching the axis, which is a riding RATIO (e.g. GOR) when the
 * cum product itself isn't selected; resolving that would draw a phantom swatch for
 * an unplotted product (EVA-6766). The style map is keyed by the selected products,
 * so a label that resolves ⇒ the cum product is selected.
 *
 * Tries: a direct hit, then `SERIES_LABEL_ALIASES` (backend label form → internal key,
 * e.g. "Total (O+C+W)" → "Total Fluid"), then plain label↔key equivalence via
 * ALL_PRODUCT_TYPES (e.g. "Oil + Cond" → "Oil", which has no parens/alias to bridge it).
 */
function resolveSegmentStyleKey(
  seg: AxisProductSegment,
  styleMap: Map<string, string | number[]>
): string | undefined {
  if (styleMap.has(seg.label)) return seg.label;
  const styleKeys = Array.from(styleMap.keys());
  const aliasMatch = styleKeys.find((k) => getSeriesLabelAliases(k).includes(seg.label));
  if (aliasMatch) return aliasMatch;
  const labelCanon = toCanonicalProductKey(seg.label);
  return styleKeys.find((k) => toCanonicalProductKey(k) === labelCanon);
}

/**
 * The cum-product names one title piece can stand for: the piece without its "Cum "
 * prefix, then the same with each trailing parenthesized group stripped in turn.
 * Every intermediate form is kept because a product name can itself end in parentheses
 * ("Total (O+C+W)"), so the fully stripped form is only one of the possible readings.
 * The stripping repeats rather than running once because the chart service appends more
 * than one trailing group — a multilinear-normalized axis reads
 * "Cum Oil + Cond (Mbbl) (normalized)".
 */
function segmentProductNames(segment: string): string[] {
  const names: string[] = [];
  let current = segment.trim().replace(/^Cum\s+/, "");
  while (current && !names.includes(current)) {
    names.push(current);
    current = current.replace(/\s*\([^)]*\)\s*$/, "").trim();
  }
  return names;
}

/**
 * Every cum-product name the axis title can be read as offering, one reading per title
 * piece: the whole title, each comma-separated part, and — when the backend sent them —
 * each `productSegments` label.
 *
 * A segment label is only used when the title actually carries it. `interleaveAxisLegend`
 * skips a segment it cannot find in the title, so counting that segment here would mark
 * its product as named by a title that never shows it; the product then lost both its
 * woven swatch and its appended name.
 */
function titleProductNames(title: string, segments?: AxisProductSegment[]): string[] {
  if (typeof title !== "string") return [];
  const pieces = [
    ...(segments ?? [])
      .map((seg) => seg.label)
      .filter((label) => label && title.includes(label)),
    title,
    ...title.split(",")
  ];
  return [...new Set(pieces.flatMap(segmentProductNames))];
}

/**
 * The title forms a product can be named by: its series-label aliases, plus its own
 * display label.
 *
 * The BOE family is matched on its alias forms ONLY. The backend builds BOE titles from
 * the alias, and the family's bare label ("BOE") is a substring of both the unit "(MBOE)"
 * and of every longer BOE name, so admitting the label hands the 20:1 axis to the 6:1
 * product. Every other aliased product keeps both forms: the backend sends some of those
 * titles with the label rather than the alias ("Total Gas Inj" for "Total Gas Injection",
 * "C5" for "C5+"), and an alias-only match costs the product its woven host swatch and
 * re-appends it as a rider — the same product named twice on one axis.
 */
function productTitleForms(product: string): string[] {
  const canonicalKey = toCanonicalProductKey(product);
  const aliases = [
    ...new Set([
      ...getSeriesLabelAliases(product),
      ...getSeriesLabelAliases(canonicalKey)
    ])
  ];
  if (isBoeFamilyProduct(product) || isBoeFamilyProduct(canonicalKey)) {
    return aliases.length ? aliases : [resolveProductLabel(product)];
  }
  // EVA-270: a subscripted product label is a RENDERING of its key, not a different
  // name. The chart service writes the plain-ASCII key in axis titles ("Cum C2, C3,
  // C4", "Cum NGL_234") and formatChartTitleWithSubscript subscripts it at draw time,
  // so the label alone never matches the title: the product looked unclaimed and was
  // re-appended as a rider, naming it twice on one axis. Admit the key form ONLY for
  // products the subscript map actually rewrites, so bare keys that are risky
  // substrings of other titles ("Oil", "BOE", "Gas") stay out.
  const keyForm =
    formatChartTitleWithSubscript(canonicalKey) !== canonicalKey ? [canonicalKey] : [];
  if (!aliases.length && !keyForm.length) return [resolveProductLabel(product)];
  return [...new Set([...aliases, ...keyForm, resolveProductLabel(product)])];
}

// Exact matches outrank every substring match, whatever the form lengths.
const EXACT_MATCH_RANK = 1e6;

// One entry per product already reported. This runs on every chart render, so an
// unguarded warn would repeat for the life of the page.
const warnedUnnamedBoeProducts = new Set<string>();

/**
 * The 20:1 and 30:1 ratios have no legacy title form, so their only reading is the exact
 * string the chart service sends. A title worded any other way matches nothing, the
 * product quietly drops from host to rider, and the axis loses its swatch. Warn the first
 * time each product hits it, and only when the title itself offers an unclaimed BOE
 * reading — a ratio riding on another product's axis is normal and must stay silent.
 */
function warnUnnamedBoeProducts(
  title: string,
  productsOnAxis: string[],
  named: Set<string>,
  unclaimedNames: string[]
): void {
  const unclaimedBoe = unclaimedNames.filter((name) => name.includes("BOE"));
  if (!unclaimedBoe.length) return;
  productsOnAxis
    .filter((product) => isBoeFamilyProduct(product) && !named.has(product))
    .forEach((product) => {
      if (warnedUnnamedBoeProducts.has(product)) return;
      warnedUnnamedBoeProducts.add(product);
      // eslint-disable-next-line no-console
      console.warn(
        `Axis title "${title}" matches no title form of "${product}"; ` +
          `unclaimed reading(s): ${unclaimedBoe.join(", ")}. ` +
          `The product cannot host the axis until the title forms agree.`
      );
    });
}

/**
 * The products on the axis the title names — the cum products the axis plots against
 * (the "hosts"), as opposed to ratios merely riding on it. Everything not in this set
 * is appended to the legend with its own name and swatch.
 *
 * Highest rank wins per title reading: an exact match beats a substring one, and
 * between two substring matches the longer form wins. Both rules are needed because
 * products that share a stem sit on the same axis — the three BOE ratios all carry the
 * "boe" unit, so "Wellhead BOE" is a substring of "Wellhead BOE 20:1" and a plain
 * substring test hands that axis to the 6:1 product. The substring reading itself
 * cannot be dropped: the chart service decorates titles ("Cum Oil + Cond CDay (Mbbl)",
 * "Cum Wellhead BOE 6:1 n/a"), and an equality-only test leaves those axes hostless.
 *
 * A tie (equal top rank) credits every tied product, not just the first one checked.
 * This is not the BOE case above — that's resolved by strict length inequality, never
 * a tie. A true tie means two genuinely different products now read the SAME exact
 * title form (EVA-101: Total Fluid's cum axis is titled exactly like Oil's, "Cum Oil +
 * Cond", whenever both are selected). Crediting only the first-checked one left the
 * other looking unclaimed and wrongly re-appended as a rider (EVA-101).
 */
function productsNamedByTitle(
  title: string,
  productsOnAxis: string[],
  segments?: AxisProductSegment[]
): Set<string> {
  const forms = new Map(productsOnAxis.map((p) => [p, productTitleForms(p)]));
  const named = new Set<string>();
  const unclaimed: string[] = [];
  titleProductNames(title, segments).forEach((name) => {
    let winners: string[] = [];
    let bestRank = 0;
    productsOnAxis.forEach((product) => {
      forms.get(product)?.forEach((form) => {
        const isExact = form === name;
        if (!isExact && !name.includes(form)) return;
        const rank = (isExact ? EXACT_MATCH_RANK : 0) + form.length;
        if (rank > bestRank) {
          bestRank = rank;
          winners = [product];
        } else if (rank === bestRank && rank > 0) {
          winners.push(product);
        }
      });
    });
    if (winners.length) winners.forEach((winner) => named.add(winner));
    else unclaimed.push(name);
  });
  warnUnnamedBoeProducts(title, productsOnAxis, named, unclaimed);
  return named;
}

/** All products (from the chart's product list) whose series sit on this axis. */
export function productsForAxis(
  allProducts: string[],
  seriesForThisAxis: { label?: string; groupTitle?: string }[]
): string[] {
  return allProducts.filter((p) =>
    seriesForThisAxis.some(
      (s) =>
        s.label === p ||
        s.groupTitle === p ||
        seriesMatchesProduct(s, p, { exactAlias: true })
    )
  );
}

/**
 * Combined-axis legend: insert a line-style swatch right after each product name
 * in the backend's unit-grouped axis title (e.g. "Cum Oil + Cond, Water (Mbbl)").
 * Each swatch is styled by the segment's cum product (resolved via
 * resolveSegmentStyleKey); a segment whose cum product isn't selected gets no swatch.
 * Returns the rewritten name + rich styles, or null when there are no segments
 * (caller falls back to the single-host legend). Pure.
 *
 * ponytail: pairs swatch→title by ordered indexOf of each segment label; a label
 * not found in the title is skipped (no swatch) rather than throwing.
 */
export function interleaveAxisLegend(
  title: string,
  segments: AxisProductSegment[] | undefined,
  multiProduct: any
): { name: string; rich: Record<string, any> } | null {
  if (!segments?.length || typeof title !== "string") return null;
  const styleMap = buildProductStyleMap(multiProduct);
  const override = !!multiProduct?.properties?.overrideBinColors;

  const rich: Record<string, any> = {};
  let cursor = 0;
  let name = "";
  let inserted = 0;
  segments.forEach((seg, idx) => {
    const pos = title.indexOf(seg.label, cursor);
    if (pos < 0) return;
    const styleKey = resolveSegmentStyleKey(seg, styleMap);
    // No style key ⇒ this cum product isn't selected — its axis exists only because
    // ratios ride on it (e.g. Cum Oil + Cond carrying only Water Cut, merged with
    // another Mbbl axis). Keep its name in the title but draw NO swatch; otherwise a
    // phantom neutral swatch appears for an unplotted product (EVA-6766).
    if (!styleKey) return;
    const key = `sw${idx}`;
    const lineStyleType = styleMap.get(styleKey) ?? "solid";
    const color = swatchColor(styleKey, override);
    rich[key] = swatchRich(lineStyleType, color);
    inserted += 1;
    const insertAt = pos + seg.label.length;
    name += title.slice(cursor, insertAt) + NAME_TO_SWATCH_GAP + `{${key}|}`;
    cursor = insertAt;
  });
  if (!inserted) return null;
  name += title.slice(cursor);
  return { name, rich };
}

/**
 * End offset in `title` of the first segment label that interleaveAxisLegend draws a
 * swatch after, or -1 when no segment gets one. Not always the first segment: an
 * unselected lead (e.g. "Oil + Cond" with only Total Fluid selected) draws none. Pure.
 */
export function firstSwatchedSegmentEnd(
  title: string,
  segments: Maybe<AxisProductSegment[]>,
  multiProduct: Maybe<Partial<MultiProductFeature>>
): number {
  const name = interleaveAxisLegend(title, segments, multiProduct)?.name;
  return name ? name.indexOf(`${NAME_TO_SWATCH_GAP}{sw`) : -1;
}

/**
 * Single- or tied-host axis: insert one swatch per named host before the trailing
 * "(unit)" so the format matches the combined axes ({product} {swatch} {unit})
 * instead of appending after the unit. `products` are FE product keys (direct
 * style-map hits — no alias resolution needed). Pure.
 *
 * A plain (non-segmented) axis can have MORE THAN ONE host when two genuinely
 * different products tie for the identical title reading. Weaving one swatch per
 * tied host (rather than picking a single, array-order-dependent "winner") keeps
 * every tied product identifiable beside the axis instead of one of them silently
 * losing both its name and its swatch.
 */
export function appendAxisLegendBeforeUnit(
  title: string,
  products: string | string[],
  multiProduct: any
): { name: string; rich: Record<string, any> } | null {
  const productList = Array.isArray(products) ? products : [products];
  if (typeof title !== "string" || !productList.length) return null;
  const styleMap = buildProductStyleMap(multiProduct);
  const override = !!multiProduct?.properties?.overrideBinColors;
  const rich: Record<string, any> = {};
  const swatch = productList
    .map((product, i) => {
      const key = `sw${i}`;
      const lineStyleType = styleMap.get(product) ?? "solid";
      rich[key] = swatchRich(lineStyleType, swatchColor(product, override));
      // Every tag gets its own gap — stripAxisLegendSuffix only strips a "{swN|}"
      // tag that is individually preceded by NAME_TO_SWATCH_GAP (EVA-101: a glued
      // second tag with no gap of its own survived the strip and leaked into the
      // screenshot title when the product legend was toggled off).
      return `${NAME_TO_SWATCH_GAP}{${key}|}`;
    })
    .join("");
  // Insert before a trailing " (unit)" group; the regex only matches the LAST
  // parenthesized group at the end, so inner parens like "Total (O+C+W)" are safe.
  const unit = title.match(/\s*\([^)]*\)\s*$/);
  const name =
    unit && unit.index !== undefined
      ? title.slice(0, unit.index) + swatch + title.slice(unit.index)
      : title + swatch;
  return { name, rich };
}

/**
 * Beside-axis legend for one x-axis, returning the rewritten name + rich styles
 * (or null). Combined axes (with backend `productSegments`) get a swatch woven in
 * after each product name; a non-combined axis names one cum product (the host) and
 * gets its swatch before the unit. Pure.
 *
 * Either way, products whose series merely ride on the axis but aren't named in the
 * title — ratios like GOR/WOR, which plot against a cum product (e.g. Oil + Cond)
 * rather than owning an axis — are appended after the title with their own swatch.
 * This keeps them in the legend when axes are combined and, in the non-combined
 * case, stops the host product's name being repeated in a suffix (EVA-6766).
 */
export function buildProductLineStyleLegend(
  title: string,
  productSegments: AxisProductSegment[] | undefined,
  productsOnAxis: string[],
  multiProduct: any
): { name: string; rich: Record<string, any> } | null {
  // Resolved once per axis: the host and the riders are two readings of the same
  // match, so they cannot disagree about who the title names.
  const named = productsNamedByTitle(title, productsOnAxis, productSegments);
  const interleaved = interleaveAxisLegend(title, productSegments, multiProduct);
  if (interleaved) {
    // Combined: the title already names every segment it carries; ratios — and any
    // product whose segment is missing from the title — are the extras.
    return appendRidingProducts(interleaved, productsOnAxis, named, multiProduct);
  }
  if (!productsOnAxis.length) return null;
  // Non-combined: the title names one or more cum products (the hosts) -- usually
  // exactly one, but EVA-101 can tie two genuinely different products (Oil and Total
  // Fluid) onto the identical "Cum Oil + Cond" reading. Weave a swatch for every
  // named host, in productsOnAxis order, then append everything else riding on the
  // axis (ratios). The host swatch(es) are only drawn when a cum product is actually
  // selected — when only ratios are on the axis (e.g. GOR/WOR/Water Cut on the
  // unselected Cum Oil + Cond), no swatch is attached to the title; the ratios are
  // simply listed. Without a host the title alone is the base (no swatch).
  const hosts = productsOnAxis.filter((p) => named.has(p));
  const base = hosts.length
    ? appendAxisLegendBeforeUnit(title, hosts, multiProduct)
    : { name: title, rich: {} };
  return base ? appendRidingProducts(base, productsOnAxis, named, multiProduct) : null;
}

/**
 * Resolve one MPC x-axis's display name + swatch rich from the backend title/segments
 * and the products on the axis, honouring `showProductLegend`. Single source of truth
 * shared by the live translators (rateCum/totalRateCum) and the screenshot re-derive,
 * so the screenshot title can't drift from the chart. `rich` is null when there's no
 * legend; callers wrap it into their own `nameTextStyle` base. Pure.
 */
export function resolveMpcXAxisName(
  title: string,
  productSegments: AxisProductSegment[] | undefined,
  productsOnAxis: string[],
  multiProduct: any,
  showProductLegend: boolean
): { name: string; rich: Record<string, any> | null } {
  const legend = showProductLegend
    ? buildProductLineStyleLegend(title, productSegments, productsOnAxis, multiProduct)
    : null;
  return {
    name: formatChartTitleWithSubscript(legend ? legend.name : title),
    rich: legend ? legend.rich : null
  };
}

/**
 * Screenshot helper: re-derive each MPC x-axis's name + swatch rich from the backend
 * layout and the SCREENSHOT's own product-legend setting — independent of the chart's
 * frozen render. Uses the same `resolveMpcXAxisName` as the live translators, so it
 * can't diverge from the chart, but is driven by the screenshot panel's
 * `showProductLegend` and never mutates the chart (so no re-render / flash). Keyed by
 * axis index (layout.xAxis2 index === ECharts xAxis `id`). Pure.
 */
export function deriveScreenshotMpcXAxisLegends(
  xAxis2: { title: string; productSegments?: AxisProductSegment[] }[] | undefined,
  series: { xAxisIndex?: number; label?: string; groupTitle?: string }[] | undefined,
  multiProduct: any,
  showProductLegend: boolean
): Record<number, { name: string; rich: Record<string, any> | null }> {
  const out: Record<number, { name: string; rich: Record<string, any> | null }> = {};
  if (!xAxis2?.length) return out;
  const allProducts = [
    ...new Set<string>([
      ...(multiProduct?.properties?.primaryProducts ?? []),
      ...(multiProduct?.properties?.secondaryProducts ?? [])
    ])
  ];
  xAxis2.forEach((axis, i) => {
    const seriesForThisAxis = (series ?? []).filter((s) => (s.xAxisIndex ?? 0) === i);
    out[i] = resolveMpcXAxisName(
      axis.title,
      axis.productSegments,
      productsForAxis(allProducts, seriesForThisAxis),
      multiProduct,
      showProductLegend
    );
  });
  return out;
}

/**
 * Append a swatch for every product on the axis the title doesn't already name —
 * e.g. ratios (GOR/WOR) riding on a cum axis. The named set comes from
 * `productsNamedByTitle`, so the host cum product (whose backend title form may differ
 * from its display label, e.g. "Total Fluid" → "Total (O+C+W)") is not re-appended.
 *
 * Each appended entry "{label} {swatch}" continues the product list (", "-joined)
 * and is inserted BEFORE the trailing "(unit)" — the unit is the shared x-axis
 * cumulative-volume unit and stays at the very end, keeping the consistent
 * "{product}{line}…{unit}" notation. Swatches are keyed `swrN` (not `swN`) so the
 * screenshot editable-gate's swatch count ignores them. Returns the base legend
 * unchanged when there's nothing extra. Pure.
 */
function appendRidingProducts(
  base: { name: string; rich: Record<string, any> },
  productsOnAxis: string[],
  namedByTitle: Set<string>,
  multiProduct: any
): { name: string; rich: Record<string, any> } {
  const styleMap = buildProductStyleMap(multiProduct);
  const override = !!multiProduct?.properties?.overrideBinColors;
  const extras = productsOnAxis.filter((p) => !namedByTitle.has(p));
  if (!extras.length) return base;

  const rich = { ...base.rich };
  const parts = extras.map((p, i) => {
    const key = `swr${i}`;
    rich[key] = swatchRich(styleMap.get(p) ?? "solid", swatchColor(p, override));
    return `${resolveProductLegendLabel(p)}${NAME_TO_SWATCH_GAP}{${key}|}`;
  });
  const appended = `, ${parts.join(", ")}`;

  // Insert before the trailing "(unit)" group so the unit stays at the end. Matches
  // the LAST parenthesized group only, so inner parens (e.g. "Total (O+C+W)") survive.
  const unit = base.name.match(/\s*\([^)]*\)\s*$/);
  const name =
    unit && unit.index !== undefined
      ? base.name.slice(0, unit.index) + appended + base.name.slice(unit.index)
      : `${base.name}${appended}`;
  return { name, rich };
}

// Every beside-axis swatch is emitted as a two-space spacer followed by a swatch
// tag (see NAME_TO_SWATCH_GAP + `{swN|}` above). Keying on the spacer+tag rather
// than a bare "{sw" avoids matching a user-authored title that contains "{sw".
const LEGEND_NAME_MARKER = "  {sw";

/** True when an axis name carries a beside-axis product-legend swatch. */
export function axisNameHasLegend(name: unknown): name is string {
  return typeof name === "string" && name.includes(LEGEND_NAME_MARKER);
}

/**
 * Removes the product-legend swatch suffix from an axis name. Used by the
 * screenshot pipeline when showProductLegend is false. No-op when the name
 * has no legend suffix.
 */
export function stripAxisLegendSuffix(name: string): string {
  if (typeof name !== "string") return name;
  // Reproduce the title the LIVE chart shows with the product legend OFF (EVA-6766):
  //  1. Drop appended ratio segments whole (", {ratio}  {swrN|}") — ratios only appear
  //     as part of the legend, so legend-off shows just the cum product(s), never GOR/WGR.
  //  2. Drop the woven cum-product swatch tokens ("  {swN|}"), keeping the product names
  //     and the trailing "(unit)" (the swatch sits before the unit, so a naive slice
  //     dropped the unit too).
  return name.replace(/, [^,]+? {2}\{swr\d+\|\}/g, "").replace(/ {2}\{sw\d+\|\}/g, "");
}

/**
 * Applies a user-authored title override while preserving the beside-axis
 * product-legend swatches where possible.
 * - No legend: apply override directly.
 * - Combined axis (2+ interleaved swatches, no "{gap|}" spacer): replace only
 *   the lead text before the first swatch; the trailing swatch tokens, product
 *   labels, and unit are preserved verbatim so the legend stays intact.
 * - Single-product or legacy suffix axis: re-attach the trailing swatch+unit
 *   suffix so the legend remains visible after the title is changed.
 */
export function preserveAxisLegendSuffix(
  overrideTitle: string,
  currentName: string
): string {
  if (!axisNameHasLegend(currentName)) return overrideTitle;
  const swatchCount = currentName.match(/\{sw\d+\|\}/g)?.length ?? 0;
  if (swatchCount === 0) {
    // Only appended ratio swatches ({swrN}) and no woven host swatch — ratios (GOR/WGR)
    // riding on a cum axis whose cum product isn't selected
    // (e.g. "Cum Oil + Cond, GOR  {swr0|} (Mbbl)"). Two cases:
    //   • Un-edited: the override still equals the cum lead (the form's default for this
    //     axis), so keep the full rendered legend — ratio names + line-style swatches —
    //     so screenshot mode matches the live chart (the EVA-6766 drop bug).
    //   • Edited: the user fully replaces the axis title with their own text, exactly
    //     like every other axis; the ratios are not re-attached.
    // The lead is the text before the first ", " (the appended-ratio boundary): a single
    // cum has no top-level comma, and a merged axis's default is its first segment.
    const decorIdx = currentName.indexOf(", ");
    const lead = decorIdx >= 0 ? currentName.slice(0, decorIdx) : currentName;
    return overrideTitle.trim() === lead.trim() ? currentName : overrideTitle;
  }
  if (swatchCount >= 2 && !currentName.includes("{gap|}")) {
    // Combined interleaved axis — replace only the first segment (the lead product
    // label before the first swatch); trailing swatch tokens and product labels are
    // preserved verbatim so the inline legend remains visible.
    const firstSwatchIdx = currentName.indexOf("  {sw");
    if (firstSwatchIdx < 0) return overrideTitle;
    return overrideTitle + currentName.slice(firstSwatchIdx);
  }
  // Single-product or legacy suffix: re-attach "  {swN|} (unit)" tail
  const markerIdx = currentName.search(/\s{2}\{sw\d+\|\}/);
  if (markerIdx < 0) return overrideTitle;
  const suffix = currentName.slice(markerIdx);
  const unit = suffix.match(/(\([^)]*\))\s*$/)?.[1];
  const title =
    unit && overrideTitle.trimEnd().endsWith(unit)
      ? overrideTitle.trimEnd().slice(0, -unit.length).trimEnd()
      : overrideTitle.trimEnd();
  return `${title}${suffix}`;
}
